export const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

export const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

const YEAR_MIN = 1900;
const YEAR_MAX = 2099;

export function getTodayMonth() {
  const today = new Date();
  return {
    year: today.getFullYear(),
    monthIndex: today.getMonth()
  };
}

/** Jul–Jun financial year containing today. */
export function getCurrentFinancialYearRange() {
  const today = getTodayMonth();
  const fromYear = today.monthIndex < 6 ? today.year - 1 : today.year;
  return {
    fromYear,
    fromMonth: 6,
    toYear: fromYear + 1,
    toMonth: 5
  };
}

export function getYearOptions() {
  const years = [];
  for (let y = YEAR_MIN; y <= YEAR_MAX; y += 1) {
    years.push(y);
  }
  return years;
}

export function formatISO(year, monthIndex, day) {
  const month = String(monthIndex + 1).padStart(2, '0');
  const dayStr = String(day).padStart(2, '0');
  return `${year}-${month}-${dayStr}`;
}

export function monthStartDate(year, monthIndex) {
  return formatISO(year, monthIndex, 1);
}

export function monthEndDate(year, monthIndex) {
  const lastDay = new Date(year, monthIndex + 1, 0).getDate();
  return formatISO(year, monthIndex, lastDay);
}

export function addMonths(year, monthIndex, monthsToAdd) {
  const date = new Date(year, monthIndex, 1);
  date.setMonth(date.getMonth() + monthsToAdd);
  return {
    year: date.getFullYear(),
    monthIndex: date.getMonth()
  };
}

export function parseISODate(isoDate) {
  if (!isoDate) return null;
  const parts = isoDate.split('-');
  if (parts.length < 2) return null;
  return {
    year: Number(parts[0]),
    monthIndex: Number(parts[1]) - 1,
    day: parts.length > 2 ? Number(parts[2]) : 1
  };
}

export function signedOffset(months, direction) {
  const magnitude = Math.max(0, Number(months) || 0);
  return direction === 'before' ? -magnitude : magnitude;
}

export function createDefaultTimeframeState() {
  const today = getTodayMonth();
  const fy = getCurrentFinancialYearRange();

  return {
    enabled: false,
    mode: 'range',
    fromYear: fy.fromYear,
    fromMonth: fy.fromMonth,
    toYear: fy.toYear,
    toMonth: fy.toMonth,
    anchorYear: today.year,
    anchorMonth: today.monthIndex,
    fromOffsetMonths: 0,
    fromOffsetDirection: 'after',
    toOffsetMonths: 6,
    toOffsetDirection: 'after'
  };
}

export function createTimeframeStateFromDates(fromIso, toIso) {
  const state = createDefaultTimeframeState();
  state.enabled = true;
  state.mode = 'range';

  const from = parseISODate(fromIso);
  const to = parseISODate(toIso);

  if (from) {
    state.fromYear = from.year;
    state.fromMonth = from.monthIndex;
  }
  if (to) {
    state.toYear = to.year;
    state.toMonth = to.monthIndex;
  }

  return state;
}

export function computeTimeframeDates(state) {
  if (!state) {
    return null;
  }

  if (state.mode === 'relative') {
    const fromSigned = signedOffset(
      state.fromOffsetMonths,
      state.fromOffsetDirection || 'after'
    );
    const toSigned = signedOffset(
      state.toOffsetMonths,
      state.toOffsetDirection || 'after'
    );
    const from = addMonths(state.anchorYear, state.anchorMonth, fromSigned);
    const to = addMonths(state.anchorYear, state.anchorMonth, toSigned);

    return {
      fromYear: from.year,
      fromMonth: from.monthIndex,
      toYear: to.year,
      toMonth: to.monthIndex,
      fromDate: monthStartDate(from.year, from.monthIndex),
      toDate: monthEndDate(to.year, to.monthIndex),
      summaryFrom: `${MONTHS_SHORT[from.monthIndex]} ${from.year}`,
      summaryTo: `${MONTHS_SHORT[to.monthIndex]} ${to.year}`,
      relativeSummary: true
    };
  }

  return {
    fromYear: state.fromYear,
    fromMonth: state.fromMonth,
    toYear: state.toYear,
    toMonth: state.toMonth,
    fromDate: monthStartDate(state.fromYear, state.fromMonth),
    toDate: monthEndDate(state.toYear, state.toMonth),
    summaryFrom: `${MONTHS_SHORT[state.fromMonth]} ${state.fromYear}`,
    summaryTo: `${MONTHS_SHORT[state.toMonth]} ${state.toYear}`,
    relativeSummary: false
  };
}

export function isRangeOrderValid(computed) {
  if (!computed) return true;
  return computed.fromDate <= computed.toDate;
}

export function formatRelativeOffsetLabel(offsetMonths, direction = 'after') {
  const magnitude = Math.max(0, Number(offsetMonths) || 0);
  if (magnitude === 0) return 'same month';
  const unit = magnitude === 1 ? 'month' : 'months';
  return direction === 'before'
    ? `${magnitude} ${unit} before`
    : `${magnitude} ${unit} later`;
}
